/**
 * The scanner source registry.
 *
 * Every entry here was checked against the live chain on 2026-08-31 before
 * being written down — `eth_getCode` for the contract, and `eth_getLogs`
 * filtered on the exact topic0 to confirm the contract actually emits the event
 * we claim it does. `docs/CONTRACT_WATCHLIST.md` carries the evidence.
 *
 * That check is not ceremony. It changed two things in the handoff spec:
 *
 *  1. **Pons is dormant.** Both launchpad factories have code, but neither has
 *     emitted `TokenLaunched` in ~20 days. Their only logs in that window are
 *     `OwnershipTransferStarted` and `OwnershipTransferred`, ~3.8 days ago —
 *     the contracts were handed over, not used. The adapter is written and
 *     registered so a revival is caught, and its evidence line says plainly
 *     that it currently produces nothing.
 *
 *  2. **The "Pons V3 factory" is not a launchpad.** `0x1f7d7550…` emits only
 *     `PoolCreated(address,address,uint24,int24,address)` — it is an ordinary
 *     Uniswap V3 factory, and it is already the busiest source of pairs we
 *     have. The spec lists it under Pons contracts; registering it as a Pons
 *     launchpad would have stamped "Pons launch" on every V3 pool on the
 *     chain. It belongs in the DEX group, and that is where it is.
 *
 * `startBlock` records when a contract was deployed. It is used as a backfill
 * floor only when `backfillFromStart` is set — see the note on that field.
 */
export const PONS_TOKEN_LAUNCHED_TOPIC = '0xdb51ea9ad51ab453a65a4cb7e60c3cb378c9501bb002609f8f97778fb6c4235a';
/**
 * Pons V2 — the bonding-curve launchpad (Phase 12, Slice 12A).
 *
 * A different product from the classic launchpad above, with a different event
 * and a different mechanism, verified live on 2026-09-10. See
 * `docs/CONTRACT_WATCHLIST.md` § *Pons V2 curve launchpad* for the evidence:
 * 1,592 launches in ~1.7h from this one address, against 2 for the classic
 * topic in the same window.
 *
 * `TokenLaunched(address indexed token, address indexed curve,
 *   address indexed deployer, address pairToken, uint256 launchConfigId,
 *   uint256 graduationThreshold)`
 */
export const PONS_V2_TOKEN_LAUNCHED_TOPIC = '0x8d4aad4953d0ca700d468f3753aa14432d1b35b43ec6409f051fb6aa43a89607';
/**
 * Bonding-curve trade events (Phase 12, Slice 12B).
 *
 * Emitted by the curve contracts themselves, not by the factory — ~32,000 an
 * hour across hundreds of addresses, so these are matched by topic across the
 * chain rather than by batching known curve addresses.
 *
 * Both shapes: 3 topics (wallet, recipient indexed), 4 data words. The word
 * order **flips** between the two — see `curve-trade-decode.ts`.
 */
export const CURVE_BUY_TOPIC = '0xec36bf571f136799e8dc0b0b8bea4b04d8bd3d43de838aab0d5fc21d4cbfc455';
export const CURVE_SELL_TOPIC = '0x8113d738abdcb6b38357e9d53a54a7157861a09031b453651f0fe7fe151f59df';
export const POOLS_TRADE_TOKEN_CREATED_TOPIC = '0x2e2b3f61b70d2d131b2a807371103cc98d51adcaa5e9a8f9c32658ad8426e74e';
export const POOLS_TRADE_TOKEN_CREATED_METADATA_TOPIC = '0x4ef8284ecf42d4cd19686572ffd87f630858c82398911e776cb831de35eddbf4';
export const POOLS_TRADE_TOKEN_LAUNCHED_TOPIC = '0x3b3d2bafdcae274a232217e1f80ee4305d3af6aa25c8b14b1681bd68d18042a4';
export const CCA_AUCTION_CREATED_TOPIC = '0x7ede475fad18ccf0039f2b956c4d43a8b4ed0853de4daaa8ae25299f331ae3b9';
export const UNISWAP_V3_POOL_CREATED_TOPIC = '0x783cca1c0412dd0d695e784568c96da2e9c22ff989357a2e8b1d9b2b4e6b7118';
export const UNISWAP_V2_PAIR_CREATED_TOPIC = '0x0d3648bd0f6ba80134a33ba9275ac585d9d315f0ad8355cddefde31afa28d0e9';
/** Native ETH sentinel used as `currency0` in Uniswap v4 pool keys. */
export const NATIVE_ETH_SENTINEL = '0x0000000000000000000000000000000000000000';
export const WETH_ADDRESS_RH = '0x0bd7d308f8e1639fab988df18a8011f41eacad73';
export const USDG_ADDRESS_RH = '0x5fc5360d0400a0fd4f2af552add042d716f1d168';
/**
 * Quote assets a launch may be denominated in.
 *
 * WETH only, for now. The wallet spends ETH, and ADR-051 already refuses a pair
 * quoted in anything else — a USDG-quoted pool would need USDG held first,
 * which is a feature rather than a missing address.
 */
export const ALLOWED_QUOTE_ASSETS = [WETH_ADDRESS_RH];
/**
 * Quote assets the scanner will *recognise* when deciding base vs quote.
 *
 * Wider than the tradable set on purpose: recognising that a USDG/TOKEN pool is
 * quoted in USDG is what lets it be classified honestly rather than dropped
 * into `DEX_POOL_CREATED` for want of a known side.
 */
export const RECOGNISED_QUOTE_ASSETS = [
    WETH_ADDRESS_RH,
    USDG_ADDRESS_RH,
    NATIVE_ETH_SENTINEL,
];
/**
 * Decimals for each recognised quote asset.
 *
 * Needed because a Pons V2 curve's `graduationThreshold` is denominated in
 * whatever that launch's `pairToken` is, and three consecutive captured
 * launches used three different ones. USDG's 6 decimals are the trap: assuming
 * 18 turns an 8,090 USDG threshold into 0.000000008090 and makes every curve
 * look permanently 0% of the way to graduating.
 *
 * Verified by direct `decimals()` reads recorded in `docs/CONTRACT_WATCHLIST.md`.
 */
export const QUOTE_ASSET_DECIMALS = {
    [WETH_ADDRESS_RH]: 18,
    [USDG_ADDRESS_RH]: 6,
    [NATIVE_ETH_SENTINEL]: 18,
};
/** The fee/tickSpacing shape pools.fun launches into on Sushi V3. */
export const POOLS_FUN_POOL_SHAPE = { fee: 10_000, tickSpacing: 200 };
/** The v4 pool key a normal pools.trade curve launch is expected to produce. */
export const POOLS_TRADE_EXPECTED_POOL_KEY = {
    currency0: NATIVE_ETH_SENTINEL,
    fee: 2500,
    tickSpacings: [25, 60],
    hooks: NATIVE_ETH_SENTINEL,
};
export const SCANNER_SOURCES = [
    // --- Pons -----------------------------------------------------------------
    {
        id: 'pons.factory.active',
        platform: 'pons',
        sourceName: 'Pons active launch factory',
        contractAddress: '0xa5aab3f0c6eeadf30ef1d3eb997108e976351feb',
        contractRole: 'launchpad_factory',
        eventName: 'TokenLaunched',
        eventTopic0: PONS_TOKEN_LAUNCHED_TOPIC,
        defaultEventKind: 'PONS_TOKEN_LAUNCHED',
        startBlock: 8991118n,
        backfillFromStart: false,
        matchMode: 'address',
        status: 'disabled',
        confidence: 'official_verified',
        evidence: '2026-08-31: 24353 bytes of code, but NO TokenLaunched in ~20M blocks (~20 days) — only ' +
            'OwnershipTransferStarted/OwnershipTransferred ~3.8 days ago. Pons had moved. Retired in ' +
            'favour of pons.topic, which matches the event signature instead of an address.',
    },
    {
        id: 'pons.factory.legacy',
        platform: 'pons',
        sourceName: 'Pons legacy launch factory',
        contractAddress: '0x0c37a24f5d23a486fa692d1500881d698b1f77a4',
        contractRole: 'launchpad_factory',
        eventName: 'TokenLaunched',
        eventTopic0: PONS_TOKEN_LAUNCHED_TOPIC,
        defaultEventKind: 'PONS_TOKEN_LAUNCHED',
        startBlock: 8600612n,
        backfillFromStart: false,
        matchMode: 'address',
        status: 'disabled',
        confidence: 'official_verified',
        evidence: '2026-08-31: 24192 bytes of code, no TokenLaunched in ~20M blocks. Superseded by ' +
            'pons.topic for the same reason as the active factory.',
    },
    {
        id: 'pons.topic',
        platform: 'pons',
        sourceName: 'Pons launches (by event signature)',
        // No address: this source matches on topic0 across the whole chain.
        contractAddress: '',
        contractRole: 'launchpad_factory',
        eventName: 'TokenLaunched',
        eventTopic0: PONS_TOKEN_LAUNCHED_TOPIC,
        defaultEventKind: 'PONS_TOKEN_LAUNCHED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'topic',
        status: 'enabled',
        confidence: 'official_verified',
        evidence: '2026-08-31: 76 TokenLaunched in ~1M blocks (~1 day) from THREE distinct emitters — ' +
            '0xf4fc0cd2 (x55), 0x2cdddb0f (x16), 0x2ba793fd (x5) — none of which is either factory ' +
            'in the handoff. Emitters are 130-byte minimal proxies that Pons rotates. A real log ' +
            'decoded cleanly with the documented ABI: token MOONCAT, paired against WETH, dexFactory ' +
            '0x1f7d7550. The ABI was right; the addresses were stale.',
    },
    // --- Pons V2 (bonding curves) --------------------------------------------
    //
    // Where the launches actually are. Registered `disabled`: ~940 launches an
    // hour against a shared, already rate-limited RPC endpoint is a capacity
    // decision for the operator, not a configuration default (ADR-062).
    {
        id: 'pons_v2.curve_factory',
        platform: 'pons_v2',
        sourceName: 'Pons V2 curve launchpad',
        contractAddress: '0x7ed598bcef8bd9edd8c97a195c6d13f40801ec7e',
        contractRole: 'launchpad_factory',
        eventName: 'TokenLaunched',
        eventTopic0: PONS_V2_TOKEN_LAUNCHED_TOPIC,
        defaultEventKind: 'PONS_V2_CURVE_LAUNCHED',
        startBlock: null,
        backfillFromStart: false,
        // Address-scoped, unlike `pons.topic`. All 1,592 sampled logs came from
        // this single address — this factory does not rotate proxies the way the
        // classic launchpad did, so there is nothing to gain from an unfiltered
        // chain-wide topic scan and a great deal of RPC cost to lose.
        matchMode: 'address',
        status: 'disabled',
        confidence: 'official_verified',
        evidence: '2026-09-10: 24177 bytes of code. 1592 TokenLaunched in 60000 blocks (~1.7h), every one ' +
            'from this address — vs 2 for the classic Pons topic in the same window. 10 launches in ' +
            '300 blocks (~30s) on a second sample. Three consecutive launches decoded cleanly and ' +
            'carried THREE different pairTokens: USDG (6dp, threshold 8090 USDG), an unverified token ' +
            '0xd0601ce1, and native ETH (threshold 4.2 ETH). Curve launches are not router-tradable; ' +
            'this is a discovery source only. See docs/CONTRACT_WATCHLIST.md.',
    },
    // --- pools.trade ----------------------------------------------------------
    {
        id: 'pools_trade.entry.current',
        platform: 'pools_trade',
        sourceName: 'pools.trade launch entry (current)',
        contractAddress: '0x0000ffffbe8efe702c8703ae3477ff5de3d319c0',
        contractRole: 'launch_entry',
        eventName: 'TokenCreated(address)',
        eventTopic0: POOLS_TRADE_TOKEN_CREATED_TOPIC,
        defaultEventKind: 'POOLS_TRADE_TOKEN_CREATED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'primary_docs',
        evidence: '2026-08-31: 4127 bytes of code. 16 TokenCreated in the last ~40k blocks (~1 hour). Active.',
    },
    {
        id: 'pools_trade.entry.original',
        platform: 'pools_trade',
        sourceName: 'pools.trade launch entry (original)',
        contractAddress: '0x00004c4ccc709ef590f7c81102c0689f0263d4e9',
        contractRole: 'launch_entry',
        eventName: 'TokenCreated(address)',
        eventTopic0: POOLS_TRADE_TOKEN_CREATED_TOPIC,
        defaultEventKind: 'POOLS_TRADE_TOKEN_CREATED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'primary_docs',
        evidence: '2026-08-31: 3747 bytes of code. Most recent TokenCreated ~0.4 days ago. Active but quiet.',
    },
    {
        id: 'pools_trade.token_factory',
        platform: 'pools_trade',
        sourceName: 'pools.trade token factory (metadata overload)',
        contractAddress: '0x000000e200088d55c39a11f609e5f667729ad49b',
        contractRole: 'token_factory',
        eventName: 'TokenCreated(address,(string,string,string,bytes))',
        eventTopic0: POOLS_TRADE_TOKEN_CREATED_METADATA_TOPIC,
        defaultEventKind: 'POOLS_TRADE_TOKEN_CREATED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'primary_docs',
        evidence: '2026-08-31: 13380 bytes of code. 16 metadata TokenCreated in the last ~40k blocks. Active.',
    },
    {
        id: 'pools_trade.launchpad.a',
        platform: 'pools_trade',
        sourceName: 'pools.trade launch path A (current)',
        contractAddress: '0x23f8209572b4a1c2ad88a42749e830791fb027f1',
        contractRole: 'launch_path',
        eventName: 'TokenLaunched',
        eventTopic0: POOLS_TRADE_TOKEN_LAUNCHED_TOPIC,
        defaultEventKind: 'POOLS_TRADE_INSTANT_LAUNCHED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'primary_docs',
        evidence: '2026-08-31: 10822 bytes of code. 16 TokenLaunched in the last ~40k blocks. Active.',
    },
    {
        id: 'pools_trade.launchpad.b',
        platform: 'pools_trade',
        sourceName: 'pools.trade launch path B (current)',
        contractAddress: '0xad44d55e7f8337c3ce113fbb591486e85be104b2',
        contractRole: 'launch_path',
        eventName: 'TokenLaunched',
        eventTopic0: POOLS_TRADE_TOKEN_LAUNCHED_TOPIC,
        defaultEventKind: 'POOLS_TRADE_INSTANT_LAUNCHED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'primary_docs',
        evidence: '2026-08-31: 10822 bytes of code. Most recent TokenLaunched ~0.06 days ago. Active.',
    },
    {
        id: 'pools_trade.launchpad.c',
        platform: 'pools_trade',
        sourceName: 'pools.trade launch path C (original)',
        contractAddress: '0xce57498d3474dcc244dfb6710ffbe6d4441cd2b2',
        contractRole: 'launch_path',
        eventName: 'TokenLaunched',
        eventTopic0: POOLS_TRADE_TOKEN_LAUNCHED_TOPIC,
        defaultEventKind: 'POOLS_TRADE_INSTANT_LAUNCHED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'primary_docs',
        evidence: '2026-08-31: 10822 bytes of code. Most recent TokenLaunched ~5.3 days ago. Older path, still watched.',
    },
    {
        id: 'pools_trade.launchpad.d',
        platform: 'pools_trade',
        sourceName: 'pools.trade launch path D (original)',
        contractAddress: '0x60d73b21cdf2ea846ab3d58699bbbb8f29d72491',
        contractRole: 'launch_path',
        eventName: 'TokenLaunched',
        eventTopic0: POOLS_TRADE_TOKEN_LAUNCHED_TOPIC,
        defaultEventKind: 'POOLS_TRADE_INSTANT_LAUNCHED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'primary_docs',
        evidence: '2026-08-31: 10774 bytes of code. Most recent TokenLaunched ~0.4 days ago. Active.',
    },
    {
        id: 'pools_trade.cca',
        platform: 'pools_trade',
        sourceName: 'pools.trade Crowd Launch auction factory',
        contractAddress: '0x000000001f26a0044baa66024e7b6599c61963f8',
        contractRole: 'auction_factory',
        eventName: 'AuctionCreated',
        eventTopic0: CCA_AUCTION_CREATED_TOPIC,
        defaultEventKind: 'POOLS_TRADE_CROWD_AUCTION_CREATED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'primary_docs',
        evidence: '2026-08-31: 24214 bytes of code. 10 AuctionCreated, most recent minutes before the check. Active.',
    },
    // --- DEX liquidity discovery ---------------------------------------------
    //
    // Not launch truth. These answer "did a token get liquidity", which is a
    // different question from "was a token launched".
    {
        id: 'uniswap.v3.factory.1f7d',
        platform: 'uniswap',
        sourceName: 'Uniswap V3 factory',
        contractAddress: '0x1f7d7550b1b028f7571e69a784071f0205fd2efa',
        contractRole: 'dex_factory',
        eventName: 'PoolCreated',
        eventTopic0: UNISWAP_V3_POOL_CREATED_TOPIC,
        defaultEventKind: 'DEX_POOL_CREATED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'official_verified',
        evidence: '2026-08-31: 24535 bytes of code. 143 logs sampled, every one PoolCreated, most recent the same minute. ' +
            'The handoff filed this under Pons contracts; it emits no launch event and is an ordinary V3 factory. ' +
            'Classifying it as a Pons launchpad would have labelled every V3 pool on the chain a Pons launch.',
    },
    {
        id: 'sushi.v3.factory',
        platform: 'sushi',
        sourceName: 'Sushi V3 factory',
        contractAddress: '0xe51960f1b45f1c9fb6d166e6a884f866fc70433b',
        contractRole: 'dex_factory',
        eventName: 'PoolCreated',
        eventTopic0: UNISWAP_V3_POOL_CREATED_TOPIC,
        defaultEventKind: 'DEX_POOL_CREATED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'enabled',
        confidence: 'official_verified',
        evidence: '2026-08-31: 24535 bytes of code. Registered as DEX liquidity discovery, not as a launchpad.',
    },
    // --- Disabled -------------------------------------------------------------
    {
        id: 'pools_fun.party_factory',
        platform: 'pools_fun',
        sourceName: 'pools.fun launchpad (PartyFactory)',
        contractAddress: '',
        contractRole: 'launchpad_factory',
        eventName: null,
        eventTopic0: null,
        defaultEventKind: 'POOLS_FUN_TOKEN_LAUNCHED',
        startBlock: null,
        backfillFromStart: true,
        matchMode: 'address',
        status: 'disabled_until_verified',
        confidence: 'disabled_unverified',
        evidence: 'pools.fun documents its core contracts as TBD and warns that unlisted addresses should be treated ' +
            'as fake. No address to verify, so none is recorded. Sushi V3 pools matching the 1% / tickSpacing 200 ' +
            'launch shape are classified as DEX-native candidates, never as pools.fun launches.',
    },
];
/**
 * Sources the scanner will actually poll.
 *
 * An address-scoped source needs an address; a topic-scoped one needs a topic
 * and deliberately has no address. Requiring an address unconditionally — as an
 * earlier version did — would silently drop the Pons topic source, which is the
 * only thing that sees Pons launches at all.
 */
export const ENABLED_SCANNER_SOURCES = SCANNER_SOURCES.filter((s) => s.status === 'enabled' &&
    (s.matchMode === 'topic' ? s.eventTopic0 !== null : s.contractAddress !== ''));
export function findSourceByAddress(address) {
    const needle = address.trim().toLowerCase();
    return SCANNER_SOURCES.find((s) => s.contractAddress === needle) ?? null;
}
/**
 * Sources that emit launch events, as opposed to liquidity events.
 *
 * The whole point of the redesign in one predicate: a launchpad tells you a
 * token was created, a DEX factory tells you a pool exists.
 */
export function isLaunchpadRole(role) {
    return (role === 'launchpad_factory' ||
        role === 'launch_entry' ||
        role === 'token_factory' ||
        role === 'launch_path' ||
        role === 'auction_factory');
}
//# sourceMappingURL=sources.js.map